/* eslint-disable no-console */
import {
  captureException,
  captureMessage,
  addBreadcrumb,
} from '../config/sentry';

type LogLevel = 'info' | 'warn' | 'error';

interface Logger {
  info: (message: string, ...args: unknown[]) => void;
  warn: (message: string, ...args: unknown[]) => void;
  error: (message: string, ...args: unknown[]) => void;
  exception: (
    error: Error | unknown,
    message?: string,
    ...args: unknown[]
  ) => void;
}

const createLogger = (context: string): Logger => {
  const log = (level: LogLevel, message: string, ...args: unknown[]) => {
    const fullMessage = `[${context}] ${message}`;

    // Sempre fazer log no console para desenvolvimento
    if (import.meta.env.VITE_NODE_ENV === 'development') {
      console[level](fullMessage, ...args);
    }

    // Adicionar breadcrumb no Sentry para rastreamento
    // O Sentry espera os níveis: 'error', 'warning', 'info', 'debug'
    // Mapeando 'warn' para 'warning' para compatibilidade
    const sentryLevel =
      level === 'warn' ? 'warning' : level === 'info' ? 'info' : level; // 'error' permanece 'error'

    addBreadcrumb(
      fullMessage,
      context,
      sentryLevel as 'error' | 'warning' | 'info' | 'debug' | undefined,
      { args }
    );

    // Enviar para Sentry baseado no nível de log
    if (level === 'error') {
      captureMessage(fullMessage, 'error', {
        tags: { context, logLevel: level },
        extra: { args },
      });
    } else if (level === 'warn') {
      captureMessage(fullMessage, 'warning', {
        tags: { context, logLevel: level },
        extra: { args },
      });
    }
  };

  const logException = (
    error: Error | unknown,
    message?: string,
    ...args: unknown[]
  ) => {
    const fullMessage = message
      ? `[${context}] ${message}`
      : `[${context}] Exception occurred`;

    // Log no console
    if (import.meta.env.VITE_NODE_ENV === 'development') {
      console.error(fullMessage, error, ...args);
    }

    // Capturar exception no Sentry
    captureException(error, {
      tags: { context, logType: 'exception' },
      extra: { message: fullMessage, args },
      level: 'error',
    });
  };

  return {
    info: (message: string, ...args: unknown[]) =>
      log('info', message, ...args),
    warn: (message: string, ...args: unknown[]) =>
      log('warn', message, ...args),
    error: (message: string, ...args: unknown[]) =>
      log('error', message, ...args),
    exception: logException,
  };
};

export { createLogger, type Logger };
